import {IonButton, IonCard, IonCardHeader, IonCol, IonInput, IonItem, IonRow} from "@ionic/react";
import {useEffect, useState} from "react";
import './DertigenMoveInput.css';
import createAxiosInstance from "../../services/AxiosInstance";
import {DertigenEvent} from "../../model/DertigenEvent";
import {User} from "../../model/User";
import { useToast } from "../../utils/useToasts";

const METHOD_NOT_ALLOWED_STATUS = 405;
const STREET_STRIPE_AMOUNT = 50;
const QUICK_ADD_STEP = 5;
const QUICK_ADD_BUTTON_COUNT = 4;
const quickAddStripeAmounts = Array.from(
    { length: QUICK_ADD_BUTTON_COUNT },
    (_, index) => (index + 1) * QUICK_ADD_STEP
);

interface DertigenMoveInputProps {
    currentPlayer?: User;
    lobbyId?: number;
    throwForPlayerId?: number;
}

const DertigenMoveInput: React.FC<DertigenMoveInputProps> = ({currentPlayer, lobbyId, throwForPlayerId}) => {
    const [amountOfStripesForNextPlayer, setAmountOfStripesForNextPlayer] = useState<string>('');
    const [amountOfStripesForSelf, setAmountOfStripesForSelf] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const axios = createAxiosInstance();
    const showToasts = useToast();

    const addStripesToSelf = (amountOfStripes: number) => {
        const move: DertigenEvent = {
            stripes: Number(amountOfStripes),
            gulps: 0,
            turnMove: true,
            thrownById: currentPlayer?.id
        }
        setSubmitting(true)
        axios.patch(`/lobbies/move/${lobbyId}/${currentPlayer?.id}`, move)
            .then(res => {
                setAmountOfStripesForSelf('')
                setAmountOfStripesForNextPlayer('')
            }).catch(err => {
            if (err.response.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', "danger")
            } else {
                showToasts('Er is iets mis gegaan', "danger")
            }
        }).finally(() => setSubmitting(false))
    }

    const doubler = () => {
        setSubmitting(true)
        axios.patch(`/lobbies/double/${lobbyId}/${currentPlayer?.id}`)
            .then(() => {
                showToasts("Je hebt de anderen verdubbeld!", "success")
            }).catch(err => {
            if (err.response.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', "danger")
            } else {
                showToasts('Er is iets mis gegaan', "danger")
            }
        }).finally(() => setSubmitting(false))
    }

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
            submitCustomPoints(event)
        }
    }

    const handleSelfInput = (value: string) => {
        setAmountOfStripesForSelf(value)
        if (value !== '' && amountOfStripesForNextPlayer !== '') {
            setAmountOfStripesForNextPlayer('')
        }
    }

    const handleNextPlayerInput = (value: string) => {
        setAmountOfStripesForNextPlayer(value)
        if (value !== '' && amountOfStripesForSelf !== '') {
            setAmountOfStripesForSelf('')
        }
    }

    const submitCustomPoints = (event?: any) => {
        if (amountOfStripesForNextPlayer !== '' && amountOfStripesForSelf !== '') {
            showToasts('Kies één invoerveld: voor jezelf of voor de volgende speler', "danger")
            return
        }

        if (amountOfStripesForNextPlayer === '' && amountOfStripesForSelf === '') {
            showToasts('Je moet een aantal strepen invullen', "danger")
            return
        }

        if (amountOfStripesForNextPlayer !== '') {
            if (!throwForPlayerId) {
                showToasts('Volgende speler niet gevonden', "danger")
                return
            }

            const move: DertigenEvent = {
                stripes: Number(amountOfStripesForNextPlayer),
                gulps: 0,
                turnMove: true,
                thrownById: currentPlayer?.id
            }

            setSubmitting(true)
            axios.patch(`/lobbies/move/${lobbyId}/${throwForPlayerId}`, move)
                .then(() => {
                    setAmountOfStripesForNextPlayer('')
                    showToasts('Strepen toegevoegd!', "success")
                    event?.target?.blur?.()
                }).catch(err => {
                if (err.response.status === METHOD_NOT_ALLOWED_STATUS) {
                    showToasts('Je hebt geen toestemming', "danger")
                } else {
                    showToasts('Er is iets mis gegaan', "danger")
                }
            }).finally(() => setSubmitting(false))
            return
        }

        const move: DertigenEvent = {
            stripes: Number(amountOfStripesForSelf),
            gulps: 0,
            turnMove: true,
            thrownById: currentPlayer?.id
        }
        setSubmitting(true)
        axios.patch(`/lobbies/move/${lobbyId}/${currentPlayer?.id}`, move)
            .then(() => {
                setAmountOfStripesForSelf('')
                showToasts('Strepen toegevoegd!', "success")
                event?.target?.blur?.()
            }).catch(err => {
            if (err.response.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', "danger")
            } else {
                showToasts('Er is iets mis gegaan', "danger")
            }
        }).finally(() => setSubmitting(false))
    }

    const addStreet = () => {
        const move: DertigenEvent = {
            stripes: STREET_STRIPE_AMOUNT,
            gulps: 0,
            turnMove: true,
            thrownById: currentPlayer?.id
        }
        setSubmitting(true)
        axios.patch(`/lobbies/move/${lobbyId}/${throwForPlayerId}`, move)
            .then(res => {
                setAmountOfStripesForNextPlayer('')
                setAmountOfStripesForSelf('')
                showToasts('Straat gegooid!', "success")
            }).catch(err => {
            if (err.response.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', "danger")
            } else {
                showToasts('Er is iets mis gegaan', "danger")
            }
        }).finally(() => setSubmitting(false))
    }


    useEffect(() => {
    }, []);

    return (
        <div className="move-input-shell" aria-live="polite">
            <IonCard className="move-turn-card">
                <IonCardHeader>{currentPlayer?.name} is aan de beurt</IonCardHeader>
            </IonCard>

            <section className="move-section">
                <p className="move-section-title">Snel toevoegen (voor gooier)</p>
                <IonRow class='stripes-button-box'>
                    {quickAddStripeAmounts.map((amount) => (
                        <IonButton
                            key={amount}
                            className='stripes-to-self'
                            onClick={() => addStripesToSelf(amount)}
                            disabled={submitting}
                        >
                            +{amount}
                        </IonButton>
                    ))}
                </IonRow>
            </section>

            <section className="move-section">
                <p className="move-section-title">Handmatig invoeren</p>
                <p className="move-input-hint">Kies één veld tegelijk: voor jezelf of voor de volgende speler.</p>

                <div className={'input-container'}>
                    <IonCol className="input-col">
                        <label className="move-input-label">Voor gooier</label>
                        <IonItem className="input-for-self">
                            <IonInput
                                type="number"
                                inputmode="numeric"
                                value={amountOfStripesForSelf}
                                placeholder="Bijv. 12"
                                onIonInput={e => handleSelfInput(e.detail.value || '')}
                                onKeyDown={handleKeyDown}
                                enterkeyhint="done"
                                disabled={submitting}
                            />
                        </IonItem>
                    </IonCol>
                    <IonCol className="input-col">
                        <label className="move-input-label">Volgende speler</label>
                        <IonItem className="input-for-next">
                            <IonInput
                                type="number"
                                inputmode="numeric"
                                value={amountOfStripesForNextPlayer}
                                placeholder="Bijv. 8"
                                onIonInput={e => handleNextPlayerInput(e.detail.value || '')}
                                onKeyDown={handleKeyDown}
                                enterkeyhint="done"
                                disabled={submitting}
                            />
                        </IonItem>
                    </IonCol>
                </div>

                <IonButton
                    className="manual-submit-button"
                    expand="block"
                    onClick={() => submitCustomPoints()}
                    disabled={submitting || (amountOfStripesForNextPlayer === '' && amountOfStripesForSelf === '')}
                >
                    Strepen toevoegen
                </IonButton>
            </section>

            <section className="move-section move-specials-section">
                <p className="move-section-title">Specials</p>
                <IonRow class='special-button-box'>
                    <IonButton className='others' onClick={() => doubler()} disabled={submitting}>X 2</IonButton>
                    <IonButton className='others' onClick={() => addStreet()} disabled={submitting}>Straatje</IonButton>
                </IonRow>
            </section>
        </div>
    );
};

export default DertigenMoveInput;
